<template>
  <div>
    <FormSubmitPopup
    :title="title"
    v-model="value"
    :rightMethod="onSubmitted"
    @popupClose="$emit('input', false)"
    :rightButtonText="$t('confirm')"
    hasAlert
    ref="formPopup"
    large>
    <template v-slot:input>
      <v-container fluid>
        <v-layout row>
          <v-flex>
            <v-card width="100%">
              <v-card-text>
                <v-layout align-baseline justify-space-between wrap>
                  <v-flex class="title" md2>{{ $t('templateInfo') }}: </v-flex>
                  <v-flex md10>
                    <v-layout justify-space-between align-baseline>
                      <v-flex md2 xs5>
                        <v-text-field
                          hide-details
                          :label="$t('templateName')"
                          v-model="name"
                          :rules="[fieldIsRequired('template')]"
                          class="required_field"></v-text-field> 
                      </v-flex>
                      <v-flex md2 xs5>
                        <v-text-field
                          hide-details
                          :label="$t('senderName')"
                          v-model="from.fullName"
                          :rules="[fieldCharLimit('Sender name', 35)]"></v-text-field> 
                      </v-flex>
                      <v-flex md2 xs5>
                        <v-text-field
                          hide-details
                          :label="$t('companyName')"
                          v-model="from.company"></v-text-field> 
                      </v-flex>
                      <v-flex md2 xs5>
                        <v-text-field
                          hide-details
                          :label="$t('productQty')"
                          v-model="quantity"></v-text-field> 
                      </v-flex>
                      <v-flex md2 xs5>
                        <v-text-field
                          :rules="[fieldIsNoLessThanZero('maxQuantity'), fieldIsInteger('maxQuantity'), () => (quantityLimit ? quantityLimit >= quantity || 'Max quantity should be no less than product quantity': true)]"
                          :hint="$t('maxQuantityHint',{maxQuantity: quantityLimit})"
                          :label="$t('maxQuantity')"
                          v-model.number="quantityLimit"></v-text-field> 
                      </v-flex>
                    </v-layout>
                    <v-layout align-center>
                      <v-flex md11 xs5>
                        <v-combobox
                          hide-details
                          label="sku"
                          chips
                          multiple
                          deletable-chips
                          v-model="sku"
                          :suffix="sku.length.toString()"
                          >
                          <template v-slot:input="data">
                            <v-chip
                              :selected="data.selected"
                              close
                              @input="removeChip(data.item)"
                            >
                              <strong>{{ data.item }}</strong>
                            </v-chip>
                          </template>
                        </v-combobox> 
                      </v-flex>
                      <v-flex md1 xs5>
                        <v-btn style="transform: translateY(7px)" dark color="primary" @click.stop="showBatchAddSkuDialog">
                          {{ $t('batch') }}
                        </v-btn>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex>
            <v-card width="100%" height="100%">
              <v-card-text>
                <v-layout class="title">
                  {{ $t('labelDetails') }}:
                </v-layout>
                <v-data-table
                  :headers="packageHeaders"
                  :items="packages"
                  hide-actions
                  class="elevation-1 mt-2"
                  item-key="index"
                >
                  <template v-slot:headerCell="props">
                    <span v-if="props.header.value === 'action'">
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn color="primary" flat small icon v-on="on" @click="addPackage">
                            <v-icon>add</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('addPackage') }}</span>
                      </v-tooltip>
                    </span>
                    <span v-else>{{ props.header.text }}</span>
                  </template>
                  <template v-slot:items="props">
                    <td class="text-xs-center">
                      <v-btn v-if="props.index > 0" color="primary" flat small icon @click="removePackage(props.index)">
                        <v-icon>delete</v-icon>
                      </v-btn>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        class="table-item"
                        hide-details
                        v-model.number="props.item.weight"
                        :rules="[
                          fieldIsRequired('weight'),
                          fieldIsOverZero('weight')
                        ]"
                        :suffix="isMeasurementMetric ? 'kg' : 'Lbs'"
                      ></v-text-field>
                    </td>
                    <td class="text-xs-left">
                      <v-layout align-center>
                        <v-flex xs4 class="mr-3">
                          <v-text-field
                            class="table-item"
                            hide-details
                            v-model.number="props.item.length"
                            :suffix="isMeasurementMetric ? 'cm' : 'in'"
                            :rules="[
                              fieldIsRequired('length'),
                              fieldIsOverZero('length')
                            ]"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs4 class="mr-3">
                          <v-text-field
                            class="table-item"
                            hide-details
                            v-model.number="props.item.width"
                            :suffix="isMeasurementMetric ? 'cm' : 'in'"
                            :rules="[
                              fieldIsRequired('width'),
                              fieldIsOverZero('width')
                            ]"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs4>
                          <v-text-field
                            class="table-item"
                            hide-details
                            v-model.number="props.item.height"
                            :suffix="isMeasurementMetric ? 'cm' : 'in'"
                            :rules="[
                              fieldIsRequired('height'),
                              fieldIsOverZero('height')
                            ]"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs1>
                          <PackagingSelector
                            :packagings="packagings"
                            @select="item => onSelectPackaging(item, props.item)"
                          />
                        </v-flex>
                      </v-layout>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        class="table-item"
                        hide-details
                        :disabled="!enableInsured"
                        v-model.number="props.item.insuredValue"
                        :rules="[fieldIsNoLessThanZero('value')]"
                      ></v-text-field>
                    </td>
                  </template>
                </v-data-table>
                <v-layout justify-space-between align-baseline wrap>
                  <v-flex md4 xs5>
                    <v-select
                      hide-details
                      :items="shippingServices"
                      item-text="text"
                      :menu-props="{returnValue:'value'}"
                      v-model="shippingService"
                      :label="$t('shippingService')"
                    ></v-select>
                  </v-flex>
                  <v-flex md2 xs5>
                    <v-select
                      :disabled="!isSupportLabelStockType"
                      hide-details
                      :label="$t('labelStockType')"
                      :items="labelStockTypes"
                      item-text="text"
                      :menu-props="{returnValue:'value'}"
                      v-model="labelStockType"/>
                  </v-flex>
                  <v-flex md2 xs5>
                    <v-select
                      hide-details
                      :label="$t('signatureService')"
                      :items="signatureOptions"
                      item-text="text"
                      :menu-props="{returnValue:'value'}"
                      v-model="signature"
                    />
                  </v-flex>
                  <v-flex md2 xs5>
                    <v-autocomplete
                      hide-details
                      :label="$t('serviceType')"
                      v-model="isExpedited"
                      item-text="label"
                      item-value="value"
                      :items="[
                        {label: $t('normal'), value: false},
                        {label: $t('expedite'), value: true}
                      ]"></v-autocomplete>
                  </v-flex>
                </v-layout>
                <v-layout justify-space-between wrap>
                  <v-flex xs5>
                    <SelectWidget
                      :label="$t('otherServices')"
                      :items="otherServices"
                      v-model="selectedOtherServices"
                      itemText='displayName'
                      itemValue='value'
                      :mutuallyExclusives="['photo', 'photoForItem']"
                      chipText='displayName'
                      :displayQty="3"/>
                  </v-flex>
                   <v-flex xs5>
                    <v-text-field
                      :label="$t('memo')"
                      :hint="$t('memoPrinted', { flexibleVars: flexibleVars.join(', ') })"
                      :rules="[fieldCharLimit('Memo', 40)]"
                      v-model="memo"
                      counter="40"></v-text-field>
                  </v-flex>
                </v-layout>
                <v-layout>       
                  <v-flex md3>
                    <v-autocomplete
                      style="margin-bottom: 0;"
                      :label="$t('instructionTemplate')"
                      @input="changeInstruction"
                      :items="instructions"
                      item-text="name"
                      return-object
                      clearable></v-autocomplete>
                  </v-flex>     
                </v-layout>
                <v-layout>
                  <v-textarea
                    :label="$t('shipmentInstruction')"
                    outline
                    v-model="instructionContent"
                    rows="8"
                    class="thinBox"></v-textarea>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
        <v-layout row v-if="isSupportDangerousGoods">
          <v-flex>
            <v-card width="100%">
              <v-card-text>
                <HazardousCommodities 
                  v-model="hazMatsInfo" 
                  :dangerousGoods.sync="dangerousGoods"
                  :offerorName.sync="offerorName" 
                  :emergencyContactNumber.sync="emergencyContactNumber" 
                  :dryIceWeight.sync="dryIceDetail.weight" 
                  :dryIceUnit.sync="dryIceDetail.unit"
                />
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
      </v-container>
    </template>
  </FormSubmitPopup>
    <FormSubmitPopup
      :title="$t('batch')"
      v-model="batchAddSkuDialog"
      :rightMethod="confirmBatchAddSku"
      @popupClose="closeBatchAddSkuDialog"
      :rightButtonText="$t('confirm')"
      hasAlert
      ref="batchAddSkuFormPopup"
      medium>
      <template v-slot:input>
        <v-container fluid>
          <v-layout row>
            <v-flex>
              <v-textarea
                outline
                v-model="batchSku"
                rows="8"
                class="thinBox">
              </v-textarea>
            </v-flex>
          </v-layout>
        </v-container>
      </template>
    </FormSubmitPopup>
  </div>
</template>

<script>
import FormSubmitPopup from './FormSubmitPopup'
import { timeTools, checkRules, cloneDeep, measurementTools } from '@/utils/tools'
import SelectWidget from './SelectWidget'
import HazardousCommodities from './HazardousCommodities'
import PackagingSelector from './PackagingSelector'

export default {
  name: 'LabelTemplatePopup',
  components: {
    FormSubmitPopup,
    SelectWidget,
    HazardousCommodities,
    PackagingSelector
  },
  mixins: [
    checkRules,
    timeTools,
    measurementTools
  ],
  data () {
    return {
      title: this.$t('createTemplate'),
      packaging: '',
      signature: false,
      newSenderName: '',
      from: {},
      shippingService: '',
      isSaveFrom: false,
      addressInEdit: {},
      memo: '#{orderId}',
      name: '',
      sku: [],
      quantity: null,
      isExpedited: false,
      selectedOtherServices: [],
      instruction: {},
      instructionContent: '',
      dangerousGoods: '',
      hazMatsInfo: [],
      offerorName: '',
      emergencyContactNumber: '',
      dryIceDetail: {
        weight: '',
        unit: ''
      },
      labelStockType: 'STOCK_4X6',
      quantityLimit: 0,
      batchAddSkuDialog: false,
      batchSku: '',
      // New packages array structure
      packages: [
        {
          length: 0,
          width: 0,
          height: 0,
          weight: 0,
          insuredValue: 0
        }
      ]
    }
  },
  watch: {
    isSupportLabelStockType: {
      handler (value) {
        if (value) return
        this.labelStockType = 'STOCK_4X6'
      },
      immediate: true
    },
    signatureOptions (value) {
      let defaultValue = value[0].value || ''
      for (let option of value) {
        if (option.default) defaultValue = option.value
        if (option.value === this.signature) return
      }
      this.signature = defaultValue
    }
  },
  computed: {
    currentShippingService () {
      return this.labelServices.find(service => 
        service.serviceTypes.some(serviceType => this.shippingService === serviceType.value)
      ) || {}
    },
    isSupportLabelStockType () {
      return this.currentShippingService.isSupportLabelStockType
    },
    isSupportDangerousGoods () {
      const isSupportDangerousGoods = this.labelServices.some(service => {
        const serviceType = service.serviceTypes.find(serviceType => this.shippingService === serviceType.value)
        return serviceType && serviceType.isSupportDangerousGoods
      })
      return this.visibleBetaFeatures.includes('hazmat') && isSupportDangerousGoods
    },
    visibleBetaFeatures () {
      return this.$store.getters.visibleBetaFeatures
    },
    flexibleVars () {
      return this.$store.getters.labelTemplateFlexibleVars.map(item => `#{${item}}`)
    },
    packagings () {
      return this.$store.getters.tenantPackagingsWithWarehousePackagings
    },
    otherServices () {
      return this.$store.getters.otherServices
    },
    instructions () {
      return this.$store.getters.tenant.instructions || []
    },
    labelServices () {
      return this.$store.getters.labelServices
    },
    shippingServices () {
      return this.labelServices
        .reduce((services, {serviceTypes = []}) => [
          ...services,
          ...serviceTypes,
          { divider: true }
        ], [])
    },
    serviceSettings () {
      return this.getLabelServiceSettingByCarrierAndServiceTypeValue(this.carrier, this.shippingService)
    },
    enableInsured () {
      return this.serviceSettings.enableInsured
    },
    carrier () {
      return (this.labelServices.find(service => 
        service.serviceTypes.find(serviceType => 
          this.shippingService === serviceType.value || 
          this.serviceType === service.carrier
        )
      ) || {}).carrier
    },
    signatureOptions () {
      return this.serviceSettings.signatureOptions
    },
    isMeasurementMetric () {
      return !!this.$store.getters.isMeasurementMetric
    },
    labelStockTypes () {
      return this.$store.getters.labelStockTypes
    },
    packageHeaders () {
      return [
        { text: this.$t('action'), value: 'action', align: 'center', sortable: false },
        { text: this.$t('weight'), value: 'weight', align: 'left', sortable: false },
        { text: `${this.$t('length')} / ${this.$t('width')} / ${this.$t('height')}`, value: 'packaging', align: 'left', sortable: false },
        { text: this.$t('insuredValue'), value: 'insuredValue', align: 'left', sortable: false }
      ]
    }
  },
  mounted () {
    if (this.initTemplate) {
      const {isMeasurementMetric, weightLb = 0, quantityLimit = 0, quantity, sku, packages, packaging, ...rest} = this.initTemplate
      if (packages && Array.isArray(packages) && packages.length > 0) {
        Object.assign(this, cloneDeep(rest))
        this.packages = packages.map(pkg => ({
          length: pkg.length || 0,
          width: pkg.width || 0,
          height: pkg.height || 0,
          weight: this.convertWeight(pkg.weight || 0, isMeasurementMetric),
          insuredValue: pkg.insuredValue || 0
        }))
      } else {
        Object.assign(this, cloneDeep(rest))
        const pkg = {
          length: rest.length || 0,
          width: rest.width || 0,
          height: rest.height || 0,
          weight: this.convertWeight(weightLb, isMeasurementMetric),
          insuredValue: rest.insuredValue || 0
        }
        this.packages = [pkg]

        // Apply packaging if exists
        if (packaging) {
          const targetPackaging = this.packagings.find(item => item.id === packaging)
          if (targetPackaging) {
            const {height, length, width, isMeasurementMetric: isPackagingMeasurementMetric = false} = targetPackaging
            Object.assign(pkg, this.convertPackagingSize({height, length, width}, isPackagingMeasurementMetric))
          }
        }
      }

      this.instructionContent = this.initTemplate.instruction || ''
      this.quantity = quantity || 1
      this.quantityLimit = quantityLimit
      this.sku = sku ? sku.split(',') : []
    }
  },
  methods: {
    confirmBatchAddSku () {
      const skus = this.batchSku.split('\n').filter(s => s)
      this.sku = [...new Set(this.sku.concat(skus))]
      this.closeBatchAddSkuDialog()
    },
    closeBatchAddSkuDialog () {
      this.batchAddSkuDialog = false
      this.batchSku = ''
    },
    showBatchAddSkuDialog () {
      this.batchAddSkuDialog = true
    },
    addPackage () {
      this.packages.push({
        length: 0,
        width: 0,
        height: 0,
        weight: 0,
        insuredValue: 0
      })
    },
    removePackage (index) {
      if (this.packages.length > 1) {
        this.packages.splice(index, 1)
      }
    },
    onSelectPackaging (e, item) {
      const {height, length, width, weight, isMeasurementMetric = false} = e
      Object.assign(item, this.convertPackagingSize({height, length, width}, isMeasurementMetric))
      if (weight) {
        this.$set(item, 'weight', this.convertWeight(weight, isMeasurementMetric))
      }
      this.menu = []
    },
    extractPayload (isBattery) {
      let {
        signature,
        packaging,
        from,
        shippingService,
        memo,
        name,
        sku,
        isExpedited,
        selectedOtherServices,
        instructionContent,
        dangerousGoods,
        hazMatsInfo,
        offerorName,
        emergencyContactNumber,
        isSupportDangerousGoods,
        isSupportLabelStockType,
        labelStockType,
        dryIceDetail,
        quantity,
        quantityLimit,
        packages
      } = this

      const skuAfterJoin = sku.join(',')
      const payload = {
        signature,
        weightLb: 0,
        isMeasurementMetric: this.isMeasurementMetric,
        packaging,
        from,
        shippingService: shippingService || '',
        memo,
        name: name.trim(),
        sku: skuAfterJoin,
        skuQty: skuAfterJoin.length,
        isExpedited,
        selectedOtherServices,
        instruction: instructionContent,
        insuredValue: 0,
        key: this.initTemplate.key || '',
        quantity: +quantity,
        quantityLimit: quantityLimit,
        packages: packages.map(pkg => ({
          length: pkg.length || 0,
          width: pkg.width || 0,
          height: pkg.height || 0,
          weight: pkg.weight || 0,
          insuredValue: this.enableInsured ? pkg.insuredValue : 0
        }))
      }
      if (isSupportLabelStockType) payload.labelStockType = labelStockType
      if (isSupportDangerousGoods) {
        payload.dangerousGoods = dangerousGoods
        if (isBattery) {
          Object.assign(payload, {
            hazMatsInfo,
            offerorName,
            emergencyContactNumber
          })
        } else if (dangerousGoods === 'DRY_ICE') {
          Object.assign(payload, {
            dryIceDetail
          })
        }
      }
      return payload
    },
    onSubmitted () {
      const { dangerousGoods, isSupportDangerousGoods, hazMatsInfo } = this
      const isBattery = dangerousGoods === 'BATTERY'
      if (isSupportDangerousGoods && isBattery && hazMatsInfo.length < 1) {
        throw Error(this.$t('fillInAtLeastOneHazMatInformation'))
      } 
      const payload = this.extractPayload(isBattery)
      return this.$store.dispatch(JSON.stringify(this.initTemplate) === '{}' ? 'updateTemplate' : 'updateTemplate', payload)
        .then(() => {
          this.$emit('submitted', payload)
        })
    },
    changeInstruction (e) {
      this.instructionContent = e.content
    },
    getLabelServiceSettingByCarrierAndServiceTypeValue (carrier, serviceTypeValue) {
      return this.$store.getters.getLabelServiceSettingByCarrierAndServiceTypeValue(carrier, serviceTypeValue)
    }
  },
  props: {
    value: Boolean,
    initTemplate: Object
  }
}
</script>

<style>
.table-item {
  padding-bottom: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/LabelTemplatePopup.vue